import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { Subject, Observable, map, merge, Observer, filter, tap, interval, takeWhile } from "rxjs";
import { Channel } from "./channel";
import {
  IncomingMessages,
  isIncomingMessage,
  OutgoingMessages,
  InChannelMessages,
  OutChannelMessages,
} from "./message";

export type PubSubClientOptions = {
  host: string;
  appId: string;
};

export class PubSubClient {
  private readonly socket: WebSocketSubject<IncomingMessages | OutgoingMessages>;
  private readonly observer: Observer<IncomingMessages | OutgoingMessages>;
  public readonly connect$: Observable<boolean>;
  public readonly close$: Observable<Event>;
  protected readonly messages$ = new Subject<unknown>();
  private readonly channelInStream: Observable<InChannelMessages>;
  private open: boolean = false;
  private channelOutStream = new Subject<OutChannelMessages>();
  constructor(private readonly options: PubSubClientOptions) {
    if (typeof window === "undefined") {
      throw new Error("This client is only supported in the browser");
    }
    const connect = new Subject<Event>();
    const close = new Subject<Event>();
    this.socket = webSocket({
      url: `${this.options.host}`,
      openObserver: connect,
      closeObserver: close,
    });
    this.observer = {
      next: (message) => {
        this.messages$.next(message);
      },
      error: (error) => {
        this.messages$.error(error);
      },
      complete: () => {
        this.messages$.complete();
      },
    };

    this.channelInStream = this.messages$
      .asObservable()
      .pipe(filter((s): s is InChannelMessages => isIncomingMessage(s) && s.service === "channel"));
    const connectSub = connect.subscribe(() => {
      this.open = true;
      interval(30000)
        .pipe(takeWhile(() => this.open))
        .subscribe(() => {
          this.socket.next({ service: "root", action: "ping" });
        });
    });
    const closeSub = close.subscribe(() => {
      connectSub.unsubscribe();
      this.open = false;
      closeSub.unsubscribe();
    });

    this.socket.subscribe(this.observer);
    this.connect$ = merge(connect, close).pipe(
      map((event) => {
        return event.type === "open";
      }),
    );
    this.close$ = close.pipe(tap(() => {}));
    this.channelOutStream.subscribe({
      next: (m) => {
        this.socket.next({
          ...m,
          appId: this.options.appId,
        });
      },
    });
  }

  public join = (channel: string, token: string | undefined = undefined): Channel => {
    this.socket.next({
      data: { channel },
      service: "channel",
      token: token,
      appId: this.options.appId,
      action: "join",
    });

    return new Channel(
      channel,
      this.options.appId,
      this.channelInStream.pipe(filter((m) => m.data.channel === channel)),
      this.channelOutStream,
    );
  };

  public leave = (channel: Channel): void => {
    this.socket.next({
      data: { channel: channel.name },
      service: "channel",
      appId: this.options.appId,
      action: "leave",
    });
  };

  public disconnect = (): void => {
    this.socket.complete();
  };
}
